// 9s loop for the "Receive financing within hours" card (design handoff:
// drawdown-to-bank-animation.md). A "Drawdown" button is pressed, the funds
// ring drops from 100% to 70%, a line draws to the bank tile and its balance
// counts up. Pure frame function so the timeline can be unit tested.

export const DRAWDOWN_LOOP_SECONDS = 9;
export const DRAWDOWN_LIMIT = 200_000;
export const DRAWDOWN_AMOUNT = 60_000;
export const BANK_START_BALANCE = 92_000;
/** End state, shown to reduced-motion visitors. */
export const DRAWDOWN_REST_SECONDS = 6.5;

const RESET_AT = 8.2;
const RING_GAP = 0.04;

const clamp = (x: number) => Math.min(1, Math.max(0, x));
const ease = (x: number) => 1 - (1 - clamp(x)) ** 3;
const easeInOut = (x: number) => {
  const c = clamp(x);
  return c < 0.5 ? 4 * c * c * c : 1 - (-2 * c + 2) ** 3 / 2;
};

export interface DrawdownFrame {
  /** Fraction of the limit still available. */
  p: number;
  available: number;
  percent: number;
  ring: { fillDash: string; trackDash: string; trackOffset: number };
  pressed: boolean;
  cursor: { x: number; y: number; opacity: number };
  /** Connector line draw fraction (0 to 1). */
  line: number;
  /** Position of the travelling dot along the connector (0 to 1). */
  dotK: number;
  dotVisible: boolean;
  balance: number;
  receivedVisible: boolean;
}

export function getDrawdownFrame(elapsedSeconds: number): DrawdownFrame {
  const t = elapsedSeconds % DRAWDOWN_LOOP_SECONDS;
  const reset = t >= RESET_AT;

  const p =
    t < 2.5
      ? 1
      : t < RESET_AT
        ? 1 - ease((t - 2.5) / 1.5) * 0.3
        : 1 - (1 - ease((t - RESET_AT) / 0.6)) * 0.3;

  const k = easeInOut((t - 1) / 1.2);
  const leave = ease((t - 6.6) / 0.8);
  const clicking = t >= 2.3 && t < 2.45;
  const cursor =
    t < 6.6
      ? { x: 90 * (1 - k), y: 70 * (1 - k) + (clicking ? 2 : 0) }
      : { x: 90 * leave, y: 70 * leave };
  const cursorOpacity =
    t < 1 ? 0 : t < 1.3 ? (t - 1) / 0.3 : t < 6.6 ? 1 : 1 - leave;

  const fill = Math.max(0.001, p);
  const track = Math.max(0, 1 - p - RING_GAP * 2);

  return {
    p,
    available: Math.round((DRAWDOWN_LIMIT * p) / 1000) * 1000,
    percent: Math.round(p * 100),
    ring: {
      fillDash: `${fill} 1`,
      trackDash: `${track} 1`,
      trackOffset: -(p + RING_GAP),
    },
    pressed: t >= 2.3 && t < 2.5,
    cursor: { ...cursor, opacity: cursorOpacity },
    line: reset ? 0 : ease((t - 4) / 0.8),
    dotK: ease((t - 4.2) / 0.7),
    dotVisible: !reset && t >= 4.2 && t < 5,
    balance: reset
      ? BANK_START_BALANCE
      : BANK_START_BALANCE + ease((t - 4.6) / 1.8) * DRAWDOWN_AMOUNT,
    receivedVisible: !reset && t >= 4.6,
  };
}
